import type {
  FunctionCallActionNode,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import React, { useMemo } from "react";

export interface FunctionCallNodeProps {
  node: FunctionCallActionNode;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  /** Resolved display name from the catalog entry. */
  functionName?: string;
}

type PortRow = { input?: Port; output?: Port };

/**
 * Pairs flow in/out on the first row, then aligns remaining input/output ports two per row.
 */
function buildPortRows(ports: Port[]): PortRow[] {
  const flowIn = ports.find(
    (port) => port.id === "in" && port.direction === "input",
  );
  const flowOut = ports.find(
    (port) => port.id === "out" && port.direction === "output",
  );
  const restInputs = ports.filter(
    (port) => port.direction === "input" && port.id !== "in",
  );
  const restOutputs = ports.filter(
    (port) => port.direction === "output" && port.id !== "out",
  );

  const rows: PortRow[] = [];

  if (flowIn || flowOut) {
    rows.push({ input: flowIn, output: flowOut });
  }

  const pairCount = Math.max(restInputs.length, restOutputs.length);
  for (let index = 0; index < pairCount; index += 1) {
    rows.push({
      input: restInputs[index],
      output: restOutputs[index],
    });
  }

  return rows;
}

/**
 * Renders a function call node in the behavior canvas.
 */
const FunctionCallNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  onPortValueChange,
  isPortConnected,
  functionName,
}: FunctionCallNodeProps) => {
  const behaviorDico = dico().behavior;
  const sectionLabel = behaviorDico.sceneCatalog?.functionCall ?? "";
  const label =
    functionName?.trim() || behaviorDico.sceneCatalog?.panelEmpty || "-";

  const headerBg =
    "linear-gradient(180deg, #7ec8ff 0%, #2b7fd4 55%, #1f5f9e 100%)";

  const rows = useMemo(() => buildPortRows(node.ports), [node.ports]);

  const renderPort = (port: Port) => (
    <PortHandle
      node={node}
      port={port}
      setPortsMap={setPortsMap}
      onMouseDown={() => onPortMouseDown?.(node.id, port)}
      onMouseUp={() => onPortMouseUp?.(node.id, port)}
      showValueInput={port.direction === "input" && port.valueType !== "flow"}
      isConnected={isPortConnected?.(node.id, port.id) ?? false}
      onValueChange={(value) => onPortValueChange?.(node.id, port.id, value)}
    />
  );

  return (
    <div
      style={{
        minWidth: 280,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background: headerBg,
          textAlign: "left",
        }}
      >
        <span>{sectionLabel}</span>
        <span
          style={{
            fontWeight: 600,
            opacity: 0.95,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </span>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "8px",
        }}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {rows.map((row, index) => {
          const paired = Boolean(row.input && row.output);

          return (
            <div
              key={`row-${index}-${row.input?.id ?? ""}-${row.output?.id ?? ""}`}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                gap: 10,
                minHeight: 32,
              }}
            >
              {row.input ? (
                <div
                  style={{
                    flex: paired ? 1 : undefined,
                    width: paired || row.output ? undefined : "100%",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  {renderPort(row.input)}
                </div>
              ) : null}
              {row.output ? (
                <div
                  style={{
                    flex: paired || row.input ? 1 : undefined,
                    width: row.input ? undefined : "100%",
                    minWidth: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  {renderPort(row.output)}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const FunctionCallNode = React.memo(FunctionCallNodeComponent);
